// Settings → « Connexion & sécurité » (owner, 2026-10-21).
//
//   Mes appareils      every device signed in to MY account, « log out » one or
//                      all; my linked Google account(s).
//   Accès équipe       the allowlist: only these emails may sign in. Plus the
//                      one-week password backup switch (OFF by default).
//   Journal            who signed in, when, from which device and IP — and who
//                      was refused.
//
// Every action is checked again on the server (worker routes/loginSecurity.ts).
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/useAuthStore';
import { rememberLoginRefusal } from '@/pages/auth/loginFlow';
import { loginSecurityApi, deviceLabel, type LoginEvent } from '@/lib/loginSecurityApi';

function Card({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  );
}

const when = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

// ── Mes appareils ────────────────────────────────────────────────────────────
export function MyDevicesSection() {
  const qc = useQueryClient();
  const signOut = useAuthStore((s) => s.signOut);
  const user = useAuthStore((s) => s.user);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const { data, isLoading, error } = useQuery({ queryKey: ['my-sessions'], queryFn: loginSecurityApi.sessions });

  const one = useMutation({
    mutationFn: (id: string) => loginSecurityApi.logoutOne(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-sessions'] }),
  });
  const all = useMutation({
    mutationFn: loginSecurityApi.logoutAll,
    onSuccess: async () => {
      rememberLoginRefusal('revoked');
      await signOut();
      window.location.assign('/login');
    },
  });

  const googles = (user?.identities ?? []).filter((i) => i.provider === 'google');

  async function linkGoogle() {
    setLinkError(null);
    const { error: err } = await supabase.auth.linkIdentity({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/admin/settings#appareils` },
    });
    if (err) setLinkError('Impossible de lier un compte Google pour l’instant. Réessayez plus tard.');
  }

  return (
    <Card
      title="Mes appareils"
      description="Les appareils connectés à votre compte. Une session se ferme toute seule après 30 jours."
    >
      {isLoading && <p className="text-sm text-gray-500">Chargement…</p>}
      {error && <p className="text-sm text-red-600">Liste indisponible. Réessayez.</p>}
      <ul className="divide-y divide-gray-100">
        {(data?.sessions ?? []).map((s) => (
          <li key={s.id} className="py-3 flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900">
                {deviceLabel(s.user_agent)}
                {s.current && <span className="ml-2 text-xs font-semibold text-emerald-700">Cet appareil</span>}
              </p>
              <p className="text-xs text-gray-500">
                {s.ip ?? 'IP inconnue'} · connecté le {when(s.created_at)} · actif le {when(s.last_used_at)}
              </p>
            </div>
            {!s.current && (
              <Button variant="secondary" size="sm" loading={one.isPending && one.variables === s.id} onClick={() => one.mutate(s.id)}>
                Déconnecter
              </Button>
            )}
          </li>
        ))}
      </ul>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {!confirmAll ? (
          <Button variant="secondary" onClick={() => setConfirmAll(true)}>Déconnecter tous les appareils</Button>
        ) : (
          <>
            <span className="text-sm text-gray-700">Vous serez aussi déconnecté ici.</span>
            <Button onClick={() => all.mutate()} loading={all.isPending}>Oui, tout déconnecter</Button>
            <Button variant="secondary" onClick={() => setConfirmAll(false)}>Annuler</Button>
          </>
        )}
      </div>
      {all.isError && <p className="mt-2 text-sm text-red-600">La déconnexion n’a pas abouti. Réessayez.</p>}

      <div className="mt-5 pt-4 border-t border-gray-100">
        <p className="text-sm font-medium text-gray-900">Compte Google</p>
        {googles.length > 0 ? (
          <p className="text-xs text-gray-600 mt-0.5">
            Lié : {googles.map((g) => String(g.identity_data?.email ?? 'Google')).join(', ')}
          </p>
        ) : (
          <p className="text-xs text-gray-600 mt-0.5">
            Votre Gmail n’a pas la même adresse que ce compte ? Liez-le ici, une seule fois.
          </p>
        )}
        <Button variant="secondary" size="sm" className="mt-2" onClick={linkGoogle}>Lier un compte Google</Button>
        {linkError && <p className="mt-2 text-sm text-red-600">{linkError}</p>}
      </div>
    </Card>
  );
}

// ── Accès équipe ─────────────────────────────────────────────────────────────
export function TeamAccessSection() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['team-access'], queryFn: loginSecurityApi.team });
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'staff' | 'admin'>('staff');
  const [confirmPw, setConfirmPw] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ['team-access'] });
  const add = useMutation({
    mutationFn: () => loginSecurityApi.addEmail(email.trim().toLowerCase(), role),
    onSuccess: () => { setEmail(''); setRole('staff'); refresh(); },
  });
  const remove = useMutation({ mutationFn: loginSecurityApi.removeEmail, onSuccess: refresh });
  const pw = useMutation({
    mutationFn: (on: boolean) => loginSecurityApi.setPasswordLogin(on),
    onSuccess: () => { setConfirmPw(false); refresh(); },
  });
  const pwOn = data?.password_login_enabled === true;

  return (
    <Card
      title="Accès équipe"
      description="Seuls ces emails peuvent se connecter (avec Google). Tout autre compte Google est refusé, sans création de compte."
    >
      {isLoading && <p className="text-sm text-gray-500">Chargement…</p>}
      <ul className="divide-y divide-gray-100">
        {(data?.team ?? []).map((r) => (
          <li key={r.email} className="py-3 flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 break-all">{r.email}</p>
              <p className="text-xs text-gray-500">
                {r.kind === 'service' ? 'Robot WhatsApp (mot de passe, jamais Google)' : r.role === 'admin' ? 'Admin' : 'Équipe'}
                {r.name ? ` · ${r.name}` : ''}
                {r.kind === 'person' && !r.has_account ? ' · compte créé à sa 1re connexion Google' : ''}
              </p>
            </div>
            {r.kind === 'person' && r.email !== data?.me && (
              <Button
                variant="secondary" size="sm"
                loading={remove.isPending && remove.variables === r.email}
                onClick={() => { if (window.confirm(`Retirer l’accès de ${r.email} ? Tous ses appareils seront déconnectés.`)) remove.mutate(r.email); }}
              >
                Retirer
              </Button>
            )}
          </li>
        ))}
      </ul>

      <form
        className="mt-4 flex flex-wrap gap-2"
        onSubmit={(e) => { e.preventDefault(); if (email.includes('@')) add.mutate(); }}
      >
        <input
          type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="prenom@gmail.com" aria-label="Email à autoriser"
          className="min-w-0 flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
        />
        <select
          value={role} onChange={(e) => setRole(e.target.value as 'staff' | 'admin')} aria-label="Rôle"
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
        >
          <option value="staff">Équipe</option>
          <option value="admin">Admin</option>
        </select>
        <Button type="submit" loading={add.isPending}>Autoriser</Button>
      </form>
      {add.isError && <p className="mt-2 text-sm text-red-600">{(add.error as Error).message}</p>}
      {remove.isError && <p className="mt-2 text-sm text-red-600">{(remove.error as Error).message}</p>}

      <div className="mt-5 pt-4 border-t border-gray-100">
        <p className="text-sm font-medium text-gray-900">
          Connexion par mot de passe (secours) : {pwOn ? <span className="text-amber-700">ACTIVÉE</span> : <span className="text-gray-600">désactivée</span>}
        </p>
        <p className="text-xs text-gray-500 mt-0.5">
          Gardée une semaine en secours, puis supprimée. Le robot WhatsApp n’est pas concerné.
        </p>
        {!confirmPw ? (
          <Button variant="secondary" size="sm" className="mt-2" onClick={() => setConfirmPw(true)}>
            {pwOn ? 'Désactiver' : 'Activer en secours'}
          </Button>
        ) : (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-700">
              {pwOn
                ? 'Toute personne connectée par mot de passe sera déconnectée, vous compris.'
                : 'Le formulaire email + mot de passe réapparaît sur la page de connexion.'}
            </span>
            <Button size="sm" onClick={() => pw.mutate(!pwOn)} loading={pw.isPending}>Confirmer</Button>
            <Button variant="secondary" size="sm" onClick={() => setConfirmPw(false)}>Annuler</Button>
          </div>
        )}
      </div>
    </Card>
  );
}

// ── Journal des connexions ───────────────────────────────────────────────────
const RESULT_LABEL: Record<string, string> = {
  ok: 'Connexion',
  denied: 'Refusé : pas sur la liste',
  password_off: 'Refusé : mot de passe désactivé',
  service_no_google: 'Refusé : robot via Google',
  expired: 'Session de plus de 30 jours',
  revoked: 'Session déconnectée',
  logout_all: 'Tous les appareils déconnectés',
  logout_one: 'Un appareil déconnecté',
};

function methodLabel(e: LoginEvent): string {
  return e.method === 'google' ? 'Google' : e.method === 'password' ? 'Mot de passe' : '';
}

export function LoginLogSection() {
  const { data, isLoading } = useQuery({ queryKey: ['login-events'], queryFn: loginSecurityApi.loginEvents });
  return (
    <Card title="Journal des connexions" description="Les 200 dernières : qui, quand, appareil, IP — et les refus.">
      {isLoading && <p className="text-sm text-gray-500">Chargement…</p>}
      {!isLoading && (data?.events ?? []).length === 0 && <p className="text-sm text-gray-500">Rien pour l’instant.</p>}
      <ul className="divide-y divide-gray-100">
        {(data?.events ?? []).map((e) => (
          <li key={e.id} className="py-2.5">
            <p className="text-sm text-gray-900">
              <span className={e.result === 'ok' ? 'text-gray-900' : e.result === 'denied' ? 'text-red-700 font-medium' : 'text-amber-800'}>
                {RESULT_LABEL[e.result] ?? e.result}
              </span>
              {' · '}<span className="break-all">{e.email ?? '—'}</span>
            </p>
            <p className="text-xs text-gray-500">
              {when(e.at)} · {deviceLabel(e.user_agent)} · {e.ip ?? 'IP inconnue'}
              {methodLabel(e) ? ` · ${methodLabel(e)}` : ''}
            </p>
          </li>
        ))}
      </ul>
    </Card>
  );
}
